import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { combineLatest, filter, from, map, Observable, of, switchMap } from 'rxjs';

import { ConfigService } from '../config.service';
import { PermissionService } from '../permission.service';
import { ProfileService } from '../profile/profile.service';
import { isSecurityChampion } from '../security-champion.service';
import { PermissionAction } from '../shared/enums/permission-action.enum';
import { SiteRole } from '../shared/enums/site-role.enum';

@Injectable({
  providedIn: 'root',
})
export class MalwareGuard {
  private configService = inject(ConfigService);
  private profileService = inject(ProfileService);
  private permissionService = inject(PermissionService);
  private router = inject(Router);

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.redirectIfNotAllowed();
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.redirectIfNotAllowed();
  }

  private redirectIfNotAllowed(): Observable<boolean | UrlTree> {
    // guards run before resolvers, so make sure the api config (and its feature flags) is loaded
    return from(this.configService.loadApiConfig()).pipe(switchMap(() => this.checkAccess()));
  }

  private checkAccess(): Observable<boolean | UrlTree> {
    const malwareFeatureEnabled = this.configService.isFeatureEnabled('malware_scanning');
    if (!malwareFeatureEnabled) {
      // no one can go there if the feature is off
      return of(this.router.parseUrl('/'));
    }
    return combineLatest([
      this.profileService.getProfile().pipe(filter(Boolean)),
      this.permissionService.getProjectsWithPermissions([PermissionAction.ProjectPermissionsManage]),
    ]).pipe(map(([profile, projectsWithPermissions]) => {
      if (SiteRole.isSiteAdmin(profile) || isSecurityChampion(profile) || projectsWithPermissions.length > 0) {
        return true;
      }
      return this.router.parseUrl('/');
    }));
  }
}
